import { ApiRequestError, isApiErrorResponse } from './client';

/**
 * Shared write path for the API clients: apiGet does not send a body.
 *
 * Failures are `ApiRequestError`s carrying the HTTP status, so a caller can
 * tell a conflict (409) from any other failure - the document editor reloads
 * a chapter on 409 and keeps the user's own version visible.
 *
 * `fallbackVerb` names the action for the one case where the API sends no
 * recognisable error body at all (a non-JSON 5xx, a proxy error page): save
 * and delete need different words there, or a failed delete would tell the
 * user that saving failed.
 */
export async function sendAndUnwrap(
  url: string,
  init: RequestInit,
  fallbackVerb: string,
): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(url, { credentials: 'same-origin', ...init });
  } catch {
    throw new ApiRequestError(0, 'unknown', 'Der Dienst ist nicht erreichbar.');
  }
  if (response.ok) return response;

  const rawBody: unknown = await response
    .clone()
    .json()
    .catch(() => undefined);
  if (isApiErrorResponse(rawBody)) {
    throw new ApiRequestError(
      response.status,
      rawBody.error.code,
      rawBody.error.message,
      rawBody.error.correlationId,
    );
  }
  throw new ApiRequestError(
    response.status,
    'unknown',
    `${fallbackVerb} fehlgeschlagen (HTTP ${response.status})`,
  );
}

/** Request init for a JSON body. */
export function jsonInit(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  };
}
